import { initializeApp } from "firebase/app";
import { deleteToken, getMessaging, getToken, onMessage as rawOnMessage } from "firebase/messaging";

// Build config from env
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_APP_KEY,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
};

// Safely initialize Firebase only when required config is present
let app = null;
let messaging = null;

try {
  const hasRequiredConfig =
    firebaseConfig.apiKey &&
    firebaseConfig.projectId &&
    firebaseConfig.messagingSenderId &&
    firebaseConfig.appId;

  if (hasRequiredConfig) {
    app = initializeApp(firebaseConfig);
    messaging = getMessaging(app);
  } else {
    if (process.env.NODE_ENV === "development") {
      // In dev, log once to console; in prod this stays silent
      // eslint-disable-next-line no-console
      console.warn(
        "Firebase messaging not initialized: missing required config (apiKey/projectId/messagingSenderId/appId)."
      );
    }
  }
} catch (err) {
  // Swallow initialization errors so the admin panel UI keeps working
  // eslint-disable-next-line no-console
  console.error("Firebase initialization failed. Messaging will be disabled.", err);
  app = null;
  messaging = null;
}

// 👇 This function requests and returns the FCM token
export const requestForToken = async () => {
  if (!messaging) {
    // Messaging not configured; fail silently
    return null;
  }

  try {
    const token = await getToken(messaging, {
      vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY,
    });

    if (token) {
      return token;
    } else {
      return null;
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error("❌ Error retrieving FCM token:", err);
    return null;
  }
};

const unregisterFCM = async () => {
  if (!messaging) {
    // Nothing to unregister
    return;
  }

  try {
    const currentToken = await getToken(messaging, {
      vapidKey: process.env.REACT_APP_VAPID_KEY,
    });

    if (currentToken) {
      await deleteToken(messaging);

      const swReg = await navigator.serviceWorker.getRegistration();
      if (swReg) {
        await swReg.unregister();
      }
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error("❌ Error unregistering FCM:", err);
  }
};

// Safe onMessage wrapper – no-op when messaging is disabled
const onMessage =
  messaging && typeof rawOnMessage === "function"
    ? rawOnMessage
    : () => {
        // noop when messaging is not available
      };

// Also export messaging + onMessage so other components can listen for pushes
export { messaging, onMessage, unregisterFCM };
